import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Lock, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invalid reset link');
      return;
    }
    if (!password || password.length < 6) {
      toast.error('Password at least 6 characters hona chahiye');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords match nahi kar rahe');
      return;
    }
    setLoading(true);
    try {
      await apiRequest('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, new_password: password }),
      });
      setSuccess(true);
      toast.success('Password successfully reset!');
      setTimeout(() => navigate('/auth'), 2500);
    } catch (error) {
      toast.error(error.message || 'Reset failed. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  // No token in URL - show error
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-lg">
            <CardContent className="p-6 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Invalid Reset Link</h2>
              <p className="text-sm text-slate-500">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Link to="/auth/forgot-password">
                <Button className="w-full bg-[#0F62FE] hover:bg-[#0050E6]" data-testid="request-new-link-btn">
                  Request a new link
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <img src={PHYSIGN_LOGO} alt="Physign" className="h-10 w-auto" />
        </Link>
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
              <Lock className="w-7 h-7 text-[#0F62FE]" />
            </div>
            <CardTitle className="text-xl">Reset Password</CardTitle>
            <CardDescription>
              {success ? 'Password updated successfully' : 'Enter your new password below'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {success ? (
              <div className="space-y-4" data-testid="reset-password-success">
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-emerald-800">
                    <p className="font-medium">Password reset successfully</p>
                    <p className="mt-1 text-emerald-700">
                      Redirecting to login in a moment...
                    </p>
                  </div>
                </div>
                <Link to="/auth">
                  <Button className="w-full bg-[#0F62FE] hover:bg-[#0050E6]">
                    Go to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <Label>New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimum 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      autoComplete="new-password"
                      autoFocus
                      data-testid="new-password-input"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`pl-10 ${confirmPassword && password !== confirmPassword ? 'border-red-400' : ''}`}
                      autoComplete="new-password"
                      data-testid="confirm-password-input"
                      required
                    />
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords match nahi kar rahe</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0F62FE] hover:bg-[#0050E6]"
                  disabled={loading || !password || password.length < 6 || password !== confirmPassword}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  Reset Password
                </Button>
                <Link to="/auth" className="block">
                  <button
                    type="button"
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />Back to Login
                  </button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
